import type { ReactNode, Key } from 'react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import cx from 'clsx';
import { formatMoney, useToast } from '@repo/design-system-kit';
import { CheckoutPanel } from '@repo/domain-kit/invoices';
import { useSegment } from '@repo/poly-hooks/segment';
import window from 'ember-window-mock';
import { useNavigate } from '@repo/poly-hooks/routing';
import { CheckoutPending } from 'receivable-invoices-js/react/components/receivable-invoices/checkout-pending';
import { CheckoutSuccess } from 'receivable-invoices-js/react/components/receivable-invoices/checkout-success';
import { LOCALES } from 'receivable-invoices-js/react/constants/locales';
import { isDeviceApplePayEnabled } from 'receivable-invoices-js/react/utils/apple-pay';
import { useLocaleManager } from 'receivable-invoices-js/react/hooks/use-locale-manager';
import { useMollie } from 'receivable-invoices-js/react/hooks/use-mollie';
import { useMedia } from 'receivable-invoices-js/react/hooks/use-media';
import { useCreatePayment } from 'receivable-invoices-js/react/api/mutations/create-payment';
import { useSentry } from 'receivable-invoices-js/react/hooks/use-sentry';
import { shouldSendToSentry } from 'receivable-invoices-js/react/utils/network';
import type { PaymentLink } from 'receivable-invoices-js/react/api/models/payment-link';
import type { PaymentPollingStatus } from 'receivable-invoices-js/react/routes/invoices/index/hooks/use-status-polling';

interface CheckoutSectionProps {
  document: {
    id: string;
    organization: {
      legalName?: string;
    };
    number?: string;
    pdfUrl?: string;
  };
  paymentLink: PaymentLink;
  hasAttachments?: boolean;
  isClientPortalEnabled?: boolean;
  clientId?: string;
  downloadAll?: () => void;
  paymentPollingStatus?: PaymentPollingStatus;
  className?: string;
  children?: ReactNode;
}

export function CheckoutSection({
  document,
  paymentLink,
  hasAttachments,
  isClientPortalEnabled,
  clientId,
  downloadAll,
  paymentPollingStatus,
  className,
  children,
}: CheckoutSectionProps & { children?: React.ReactNode }) {
  const { formatMessage, locale } = useIntl();
  const localeManager = useLocaleManager();
  const mollie = useMollie();
  const segment = useSegment();
  const navigate = useNavigate();
  const { isTablet } = useMedia();
  const hideDownloadButton = Boolean(!document.pdfUrl);
  const sentry = useSentry();
  const { showToast } = useToast();

  const { mutate: createPayment, isPending: isCreatingPayment } = useCreatePayment({
    onSuccess: externalLink => {
      if (externalLink) {
        window.location.href = externalLink;
      }
    },
    onError: error => {
      if (shouldSendToSentry(error)) {
        sentry.captureException(error);
      }
      showToast({ text: formatMessage({ id: 'toasts.errors.try-later' }), type: 'error' });
    },
  });

  const paymentLinkAmount = paymentLink.amount;
  const formattedAmount =
    paymentLinkAmount.value && paymentLinkAmount.currency
      ? formatMoney({
          amount: paymentLinkAmount.value,
          currency: paymentLinkAmount.currency,
        })
      : undefined;

  const resource = {
    amount: formattedAmount,
    organizationName: document.organization.legalName ?? '',
    invoiceNumber: document.number,
  };

  const paymentLinkValues = {
    // Cast is safe: 'canceled' status is filtered out in routes/invoices/index/use-route-query.ts, and 'expired' is mapped to 'open' above
    status: (paymentLink.status === 'expired' ? 'open' : paymentLink.status) as 'open' | 'paid',
    methods: paymentLink.potentialPaymentMethods,
  };

  const locales = LOCALES.map(item => ({
    id: item.code,
    label: item.name,
  }));

  const pdfUrl = document.pdfUrl;

  const isPaymentLinkPaid = paymentLink.status === 'paid';

  const downloadLabel = hasAttachments
    ? formatMessage({ id: 'invoice.preview.cta.download-all' })
    : '';

  const isApplePayAvailable = isDeviceApplePayEnabled();

  const updateLocale = useCallback(
    (code: Key) => {
      localeManager.setLocale(code as string);
    },
    [localeManager]
  );

  const onCreditCardSubmit = useCallback(
    (token: string) => {
      segment.track('public-page_pay_clicked');

      const { id } = document;

      createPayment({
        resourceId: id,
        paymentMethod: 'credit_card',
        creditCardToken: token,
        locale,
      });
    },
    [document, segment, locale, createPayment]
  );

  const onMethodClick = useCallback(
    (method: string) => {
      const { id } = document;

      if (method === 'bank_transfer') {
        navigate(`invoices/${id}/pay`);
        return;
      }

      createPayment({
        resourceId: id,
        paymentMethod: method,
        locale,
      });
    },
    [document, navigate, locale, createPayment]
  );

  const renderCheckoutContent = () => {
    if (isPaymentLinkPaid) {
      return (
        <CheckoutSuccess
          amount={resource.amount}
          invoiceNumber={resource.invoiceNumber}
          isClientPortalEnabled={isClientPortalEnabled}
          locales={locales}
          onLocaleSelectionChange={updateLocale}
          organizationName={resource.organizationName}
          selectedLocale={locale}
          status={paymentLinkValues.status}
        >
          {children}
        </CheckoutSuccess>
      );
    }

    if (paymentPollingStatus && ['polling', 'timeout'].includes(paymentPollingStatus)) {
      return (
        <CheckoutPending
          amount={resource.amount}
          invoiceNumber={resource.invoiceNumber}
          isClientPortalEnabled={isClientPortalEnabled}
          locales={locales}
          onLocaleSelectionChange={updateLocale}
          organizationName={resource.organizationName}
          selectedLocale={locale}
          status={paymentLinkValues.status}
        >
          {children}
        </CheckoutPending>
      );
    }

    return (
      <CheckoutPanel
        amount={resource.amount}
        clientId={clientId}
        disabled={isCreatingPayment}
        downloadLabel={downloadLabel}
        hideDownloadButton={hideDownloadButton}
        invoiceNumber={resource.invoiceNumber}
        isApplePayAvailable={isApplePayAvailable}
        isClientPortalEnabled={isClientPortalEnabled}
        locales={locales}
        methods={paymentLinkValues.methods}
        mollie={mollie.isLoaded ? mollie : null}
        onCreditCardSubmit={onCreditCardSubmit}
        onDownloadPress={hasAttachments ? downloadAll : undefined}
        onLocaleSelectionChange={updateLocale}
        onMethodClick={onMethodClick}
        organizationName={resource.organizationName}
        pdfUrl={pdfUrl}
        selectedLocale={locale}
        status={paymentLinkValues.status}
      >
        <div className={cx(isTablet && 'mb-48')}>{children}</div>
      </CheckoutPanel>
    );
  };

  return (
    <div className={className} data-test-checkout-section>
      <div className="mb-24 md:mb-48">{renderCheckoutContent()}</div>
    </div>
  );
}
